/**
 * FeedbackModal
 * -------------
 * The "contribute to the map" form. Two flows:
 *
 *   - MACRO (target === null): propose a new Position / Move / Tag,
 *     or share General feedback. Branching form — the fields shown
 *     depend on which kind you pick. Opened from the top-bar
 *     contribute icon.
 *   - MICRO (target set): propose a correction or addition to an
 *     EXISTING node. Opened from the drawer's per-node icon,
 *     pre-scoped to that node.
 *
 * Submissions POST to Web3Forms (feedback-config.ts), relayed as
 * structured email to the curator inbox — labelled fields so it's
 * scannable by hand now and by an automated pass later.
 *
 * Spam defence: honeypot (always) + hCaptcha (when configured).
 * Portalled to document.body (escapes panel stacking, ADR-035).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { dataSource } from '../data/source';
import { useAppStore } from '../state/store';
import type { Move, Position } from '../types/domain';
import {
  HCAPTCHA_SITE_KEY,
  WEB3FORMS_ACCESS_KEY,
  WEB3FORMS_ENDPOINT,
  feedbackConfigured,
  hcaptchaEnabled,
} from './feedback-config';
import {
  Field,
  MultiSelectSearch,
  VideoList,
  chipStyle,
  inputStyle,
  type Option,
  type VideoEntry,
} from './feedback-fields';
import {
  FONT,
  GLASS,
  SURFACE,
  LINE,
  LINE2,
  INK,
  INK2,
  GOLD,
  GOLD_INK,
  BLUE,
  RADIUS,
  hexA,
} from './tokens';

/** Minimal typing for the lazily-loaded global hCaptcha API. */
type HCaptcha = {
  render: (container: HTMLElement, opts: { sitekey: string }) => string;
  getResponse: (widgetId?: string) => string;
  reset: (widgetId?: string) => void;
};
declare global {
  interface Window {
    hcaptcha?: HCaptcha;
  }
}

let hcaptchaLoad: Promise<void> | null = null;
function loadHcaptcha(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.hcaptcha) return Promise.resolve();
  if (hcaptchaLoad) return hcaptchaLoad;
  hcaptchaLoad = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://js.hcaptcha.com/1/api.js?render=explicit';
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('hCaptcha script failed to load'));
    document.head.appendChild(s);
  });
  return hcaptchaLoad;
}

// Macro branch kinds.
type MacroKind = 'position' | 'move' | 'tag' | 'general';
const MACRO_KINDS: { value: MacroKind; label: string }[] = [
  { value: 'position', label: 'Position' },
  { value: 'move', label: 'Move' },
  { value: 'tag', label: 'Tag' },
  { value: 'general', label: 'General' },
];

// Tag classification axes for the "propose a tag" flow.
const TAG_TYPES = [
  { value: 'concept', label: 'Concept' },
  { value: 'family', label: 'Family' },
  { value: 'move-category', label: 'Move category' },
  { value: 'submission-type', label: 'Submission type' },
  { value: 'posture', label: 'Posture' },
  { value: 'position-type', label: 'Position type' },
  { value: 'difficulty', label: 'Difficulty' },
  { value: 'gi', label: 'Gi / No-gi' },
  { value: 'other', label: 'Other / custom' },
] as const;

// Micro (per-node) change kinds.
const MICRO_KINDS = [
  { value: 'correction', label: 'Correction' },
  { value: 'add-video', label: 'Add video' },
  { value: 'add-detail', label: 'Add detail' },
  { value: 'add-tags', label: 'Add tags' },
] as const;

type SubmitState = 'idle' | 'sending' | 'ok' | 'error' | 'captcha';

export function FeedbackModal() {
  const feedback = useAppStore((s) => s.feedback);
  const closeFeedback = useAppStore((s) => s.closeFeedback);
  const { open, target } = feedback;
  const isMicro = target !== null;

  // ---- dataset for the search dropdowns ----
  const [positions, setPositions] = useState<Position[]>([]);
  const [moves, setMoves] = useState<Move[]>([]);
  const dataLoaded = useRef(false);
  useEffect(() => {
    if (!open || dataLoaded.current) return;
    dataLoaded.current = true;
    Promise.all([dataSource.positions(), dataSource.moves()])
      .then(([p, m]) => {
        setPositions(p);
        setMoves(m);
      })
      .catch((err) => console.warn('feedback data load failed:', err));
  }, [open]);

  const positionOptions = useMemo<Option[]>(
    () =>
      positions.map((p) => ({
        value: `position:${p.id}`,
        label: p.name,
        group: 'position',
      })),
    [positions],
  );
  const nodeOptions = useMemo<Option[]>(
    () => [
      ...positionOptions,
      ...moves.map((m) => ({
        value: `move:${m.id}`,
        label: m.name,
        group: 'move',
      })),
    ],
    [positionOptions, moves],
  );
  // Existing tags across the dataset — union of concepts, families,
  // and free tags — so users pick from real vocabulary (with the
  // option to coin a new one).
  const tagOptions = useMemo<Option[]>(() => {
    const set = new Map<string, Option>();
    const push = (v: string, group: string) => {
      const key = v.toLowerCase();
      if (!set.has(key)) set.set(key, { value: v, label: v, group });
    };
    for (const p of positions) {
      (p.concepts ?? []).forEach((c) => push(c, 'concept'));
      if (p.family) push(p.family, 'family');
      (p.tags ?? []).forEach((t) => push(t, 'tag'));
    }
    for (const m of moves) {
      (m.concepts ?? []).forEach((c) => push(c, 'concept'));
      if (m.family) push(m.family, 'family');
      (m.tags ?? []).forEach((t) => push(t, 'tag'));
      push(m.category, 'category');
    }
    return [...set.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [positions, moves]);

  // ---- form state ----
  const [macroKind, setMacroKind] = useState<MacroKind>('position');
  const [microKind, setMicroKind] = useState<string>(MICRO_KINDS[0].value);
  const [name, setName] = useState('');
  const [proposedTags, setProposedTags] = useState<string[]>([]);
  const [tagType, setTagType] = useState<string>(TAG_TYPES[0].value);
  const [appliesTo, setAppliesTo] = useState<string[]>([]);
  const [relatedItems, setRelatedItems] = useState<string[]>([]);
  const [moveFrom, setMoveFrom] = useState<string[]>([]);
  const [moveTo, setMoveTo] = useState<string[]>([]);
  const [videos, setVideos] = useState<VideoEntry[]>([]);
  const [message, setMessage] = useState('');
  const [contact, setContact] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [submit, setSubmit] = useState<SubmitState>('idle');

  // hCaptcha refs
  const captchaRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [captchaReady, setCaptchaReady] = useState(false);

  // Reset the form on each open.
  useEffect(() => {
    if (!open) return;
    setMacroKind('position');
    setMicroKind(MICRO_KINDS[0].value);
    setName('');
    setProposedTags([]);
    setTagType(TAG_TYPES[0].value);
    setAppliesTo([]);
    setRelatedItems([]);
    setMoveFrom([]);
    setMoveTo([]);
    setVideos([]);
    setMessage('');
    setContact('');
    setHoneypot('');
    setSubmit('idle');
  }, [open, target?.id]);

  // Render hCaptcha widget on open (if configured).
  useEffect(() => {
    if (!open || !hcaptchaEnabled) return;
    let cancelled = false;
    setCaptchaReady(false);
    loadHcaptcha()
      .then(() => {
        if (cancelled || !captchaRef.current || !window.hcaptcha) return;
        try {
          widgetIdRef.current = window.hcaptcha.render(captchaRef.current, {
            sitekey: HCAPTCHA_SITE_KEY,
          });
        } catch (err) {
          console.warn('hCaptcha render:', err);
        }
        setCaptchaReady(true);
      })
      .catch((err) => {
        console.warn('hCaptcha unavailable:', err);
        setCaptchaReady(true);
      });
    return () => {
      cancelled = true;
      widgetIdRef.current = null;
    };
  }, [open]);

  // Esc closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        closeFeedback();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closeFeedback]);

  if (!open) return null;

  const canSubmit = message.trim().length > 0 && submit !== 'sending';

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    if (honeypot.trim().length > 0) {
      setSubmit('ok'); // silently drop bots
      return;
    }

    let captchaToken = '';
    if (hcaptchaEnabled && window.hcaptcha) {
      captchaToken = window.hcaptcha.getResponse(
        widgetIdRef.current ?? undefined,
      );
      if (!captchaToken) {
        setSubmit('captcha');
        return;
      }
    }

    setSubmit('sending');

    // Build a labelled payload. Only the fields relevant to the
    // chosen branch are filled; the rest are omitted for a clean
    // email.
    const label = (opts: Option[], values: string[]) =>
      values
        .map((v) => opts.find((o) => o.value === v)?.label ?? v)
        .join(', ');

    const scope = isMicro
      ? `Micro — ${target.kind}: ${target.name}`
      : `Macro — ${MACRO_KINDS.find((k) => k.value === macroKind)?.label}`;

    const kindLabel = isMicro
      ? MICRO_KINDS.find((k) => k.value === microKind)?.label
      : MACRO_KINDS.find((k) => k.value === macroKind)?.label;

    const videoLines = videos
      .filter((v) => v.url.trim() || v.description.trim())
      .map(
        (v, i) =>
          `  ${i + 1}. ${v.url.trim() || '(no link)'} — ${
            v.description.trim() || '(no description)'
          }`,
      )
      .join('\n');

    const payload: Record<string, string> = {
      access_key: WEB3FORMS_ACCESS_KEY,
      subject: `bjjmap contribution — ${scope}`,
      from_name: 'bjjmap contribute form',
      contribution_scope: scope,
      contribution_kind: kindLabel ?? '',
      suggestion: message.trim(),
      contact: contact.trim() || '(none provided)',
      submitted_at: new Date().toISOString(),
    };

    if (isMicro) {
      payload.target_node = `${target.kind}:${target.id} (${target.name})`;
      if (proposedTags.length)
        payload.proposed_tags = label(tagOptions, proposedTags);
      if (videoLines) payload.videos = `\n${videoLines}`;
    } else if (macroKind === 'position') {
      payload.proposed_name = name.trim() || '(unnamed)';
      payload.proposed_tags = proposedTags.length
        ? label(tagOptions, proposedTags)
        : '(none)';
      if (videoLines) payload.videos = `\n${videoLines}`;
    } else if (macroKind === 'move') {
      payload.proposed_name = name.trim() || '(unnamed)';
      payload.proposed_tags = proposedTags.length
        ? label(tagOptions, proposedTags)
        : '(none)';
      payload.connects_from = moveFrom.length
        ? label(positionOptions, moveFrom)
        : '(unspecified)';
      payload.connects_to = moveTo.length
        ? label(positionOptions, moveTo)
        : '(unspecified)';
      if (videoLines) payload.videos = `\n${videoLines}`;
    } else if (macroKind === 'tag') {
      payload.tag_type = TAG_TYPES.find((t) => t.value === tagType)?.label ?? '';
      payload.proposed_tag = name.trim() || '(unnamed)';
      payload.applies_to = appliesTo.length
        ? label(nodeOptions, appliesTo)
        : '(unspecified)';
    } else if (macroKind === 'general') {
      payload.related_items = relatedItems.length
        ? label([...nodeOptions, ...tagOptions], relatedItems)
        : '(none)';
    }

    if (captchaToken) payload['h-captcha-response'] = captchaToken;

    try {
      const res = await fetch(WEB3FORMS_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const json = (await res.json()) as { success?: boolean };
      setSubmit(json.success ? 'ok' : 'error');
      if (!json.success && hcaptchaEnabled && window.hcaptcha) {
        window.hcaptcha.reset(widgetIdRef.current ?? undefined);
      }
    } catch (err) {
      console.error('Feedback submit failed:', err);
      setSubmit('error');
      if (hcaptchaEnabled && window.hcaptcha) {
        window.hcaptcha.reset(widgetIdRef.current ?? undefined);
      }
    }
  };

  return createPortal(
    <div
      onClick={closeFeedback}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        background: 'rgba(0,0,0,0.32)',
        backdropFilter: 'blur(2px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Contribute to bjjmap"
        style={{
          width: 'min(520px, 100%)',
          maxHeight: '88vh',
          overflowY: 'auto',
          background: GLASS.raised,
          border: `1px solid ${LINE2}`,
          borderRadius: RADIUS.lg,
          boxShadow: '0 16px 48px rgba(0,0,0,0.55)',
          padding: 20,
          color: INK,
          fontFamily: FONT.ui,
        }}
      >
        {/* Header — welcoming copy that invites participation. On the
            success screen (submit === 'ok') we drop the intro copy and
            let the "Sent — thank you" section stand on its own; only
            the close button stays in this row. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: submit === 'ok' ? 'flex-end' : 'space-between',
            gap: 12,
            marginBottom: submit === 'ok' ? 0 : 16,
          }}
        >
          {submit !== 'ok' && (
            <div>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  fontFamily: FONT.display,
                  letterSpacing: '-0.01em',
                }}
              >
                {isMicro
                  ? `Improve “${target.name}”`
                  : 'Leave the map better than you found it 🌳'}
              </div>
              <div
                style={{
                  fontSize: 12.5,
                  opacity: 0.62,
                  marginTop: 4,
                  lineHeight: 1.5,
                }}
              >
                {isMicro
                  ? 'Spot something off, or know a detail / clip that makes this node better? Share it — every addition sharpens the map for the next player.'
                  : 'bjjmap is a living atlas of Brazilian Jiu-Jitsu. Propose a position, move, tag, or give us feedback on what could be better. Everyone can help chart the grappling universe.'}
              </div>
            </div>
          )}
          <button
            onClick={closeFeedback}
            aria-label="Close"
            style={{
              flexShrink: 0,
              width: 30,
              height: 30,
              borderRadius: RADIUS.sm,
              background: SURFACE.raised,
              border: `1px solid ${LINE}`,
              color: INK2,
              cursor: 'pointer',
              fontSize: 16,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {!feedbackConfigured ? (
          <div style={{ fontSize: 13, lineHeight: 1.5, opacity: 0.8 }}>
            The contribute form isn&apos;t connected to an inbox yet. Set a
            Web3Forms access key (see <code>src/ui/feedback-config.ts</code>)
            to enable it.
          </div>
        ) : submit === 'ok' ? (
          <div style={{ padding: '10px 0' }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 600,
                marginBottom: 6,
                fontFamily: FONT.display,
                letterSpacing: '-0.01em',
              }}
            >
              Sent — thank you! 🙏
            </div>
            <div style={{ fontSize: 13, opacity: 0.72, lineHeight: 1.5 }}>
              Your contribution is on its way. Ideas like yours are exactly
              how this map keeps getting better.
            </div>
            <button onClick={closeFeedback} style={{ ...primaryBtn, marginTop: 16 }}>
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit}>
            {/* Kind selector */}
            <Field
              label={isMicro ? 'What kind of improvement?' : 'What would you like to add?'}
            >
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {(isMicro ? MICRO_KINDS : MACRO_KINDS).map((k) => {
                  const active = isMicro
                    ? microKind === k.value
                    : macroKind === k.value;
                  return (
                    <button
                      type="button"
                      key={k.value}
                      onClick={() =>
                        isMicro
                          ? setMicroKind(k.value)
                          : setMacroKind(k.value as MacroKind)
                      }
                      style={{
                        ...chipStyle,
                        background: active ? hexA(BLUE, 0.14) : 'transparent',
                        borderColor: active ? hexA(BLUE, 0.32) : LINE,
                        color: active ? INK : INK2,
                      }}
                    >
                      {k.label}
                    </button>
                  );
                })}
              </div>
            </Field>

            {/* ---- MACRO branches ---- */}
            {!isMicro && (macroKind === 'position' || macroKind === 'move') && (
              <>
                <Field label={`${macroKind === 'position' ? 'Position' : 'Move'} name`}>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={
                      macroKind === 'position'
                        ? 'e.g. Reverse De La Riva'
                        : 'e.g. Berimbolo'
                    }
                    style={inputStyle}
                  />
                </Field>

                {macroKind === 'move' && (
                  <>
                    <Field
                      label="Starts from"
                      hint="Which position(s) does this move begin in?"
                    >
                      <MultiSelectSearch
                        options={positionOptions}
                        selected={moveFrom}
                        onChange={setMoveFrom}
                        placeholder="Search positions…"
                      />
                    </Field>
                    <Field
                      label="Leads to"
                      hint="Which position(s) does it end in?"
                    >
                      <MultiSelectSearch
                        options={positionOptions}
                        selected={moveTo}
                        onChange={setMoveTo}
                        placeholder="Search positions…"
                      />
                    </Field>
                  </>
                )}

                <Field
                  label="Tags"
                  hint="Pick existing tags or coin a new one — concepts, family, category…"
                >
                  <MultiSelectSearch
                    options={tagOptions}
                    selected={proposedTags}
                    onChange={setProposedTags}
                    placeholder="Search or add tags…"
                    allowCreate
                  />
                </Field>

                <Field label="Description">
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={`Describe this ${macroKind} — what it is, when you'd use it, key details.`}
                    rows={4}
                    style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }}
                  />
                </Field>

                <Field
                  label="Videos (optional)"
                  hint="Links that show the technique. Add a timestamp in the note if the clip starts mid-video."
                >
                  <VideoList videos={videos} onChange={setVideos} />
                </Field>
              </>
            )}

            {!isMicro && macroKind === 'tag' && (
              <>
                <Field label="Tag type">
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {TAG_TYPES.map((t) => (
                      <button
                        type="button"
                        key={t.value}
                        onClick={() => setTagType(t.value)}
                        style={{
                          ...chipStyle,
                          background:
                            tagType === t.value ? hexA(BLUE, 0.14) : 'transparent',
                          borderColor:
                            tagType === t.value ? hexA(BLUE, 0.32) : LINE,
                          color: tagType === t.value ? INK : INK2,
                        }}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                </Field>

                <Field label="Tag name">
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. kuzushi, leg-entanglement, pressure-passing"
                    style={inputStyle}
                  />
                </Field>

                <Field
                  label="Applies to"
                  hint="Which positions / moves should carry this tag?"
                >
                  <MultiSelectSearch
                    options={nodeOptions}
                    selected={appliesTo}
                    onChange={setAppliesTo}
                    placeholder="Search positions & moves…"
                  />
                </Field>

                <Field label="Why this tag?">
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="What does this tag capture, and why is it worth adding?"
                    rows={4}
                    style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }}
                  />
                </Field>
              </>
            )}

            {!isMicro && macroKind === 'general' && (
              <>
                <Field
                  label="Related to (optional)"
                  hint="Any positions, moves, or tags this touches on."
                >
                  <MultiSelectSearch
                    options={[...nodeOptions, ...tagOptions]}
                    selected={relatedItems}
                    onChange={setRelatedItems}
                    placeholder="Search positions, moves, tags…"
                  />
                </Field>
                <Field label="Your idea">
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="What could be better? Anything from a data fix to a whole new feature."
                    rows={5}
                    style={{ ...inputStyle, resize: 'vertical', minHeight: 90 }}
                  />
                </Field>
              </>
            )}

            {/* ---- MICRO branch ---- */}
            {isMicro && (
              <>
                {(microKind === 'add-tags' || microKind === 'correction') && (
                  <Field
                    label="Tags (optional)"
                    hint="Tags you think this node should carry."
                  >
                    <MultiSelectSearch
                      options={tagOptions}
                      selected={proposedTags}
                      onChange={setProposedTags}
                      placeholder="Search or add tags…"
                      allowCreate
                    />
                  </Field>
                )}
                <Field
                  label="Your suggestion"
                  hint="What should change or be added, and why?"
                >
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Describe the correction or addition…"
                    rows={4}
                    style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }}
                  />
                </Field>
                {(microKind === 'add-video' || microKind === 'correction') && (
                  <Field
                    label="Videos (optional)"
                    hint="Links that show it. Add a timestamp in the note if the clip starts mid-video."
                  >
                    <VideoList videos={videos} onChange={setVideos} />
                  </Field>
                )}
              </>
            )}

            {/* Contact */}
            <Field label="Your email (optional)" hint="Only if you'd like a follow-up.">
              <input
                type="email"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                placeholder="you@example.com"
                style={inputStyle}
              />
            </Field>

            {/* Honeypot */}
            <input
              type="text"
              name="botcheck"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{
                position: 'absolute',
                left: '-9999px',
                width: 1,
                height: 1,
                opacity: 0,
              }}
            />

            {/* hCaptcha */}
            {hcaptchaEnabled && (
              <div style={{ marginBottom: 12 }}>
                <div ref={captchaRef} />
                {!captchaReady && (
                  <div style={{ fontSize: 11, opacity: 0.45 }}>
                    Loading spam check…
                  </div>
                )}
              </div>
            )}

            {submit === 'captcha' && (
              <div style={{ fontSize: 12, color: '#ffaab2', marginBottom: 10 }}>
                Please complete the spam check above before sending.
              </div>
            )}
            {submit === 'error' && (
              <div style={{ fontSize: 12, color: '#ffaab2', marginBottom: 10 }}>
                Something went wrong sending that. Please try again in a moment.
              </div>
            )}

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
                marginTop: 4,
              }}
            >
              <button type="button" onClick={closeFeedback} style={secondaryBtn}>
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSubmit}
                style={{
                  ...primaryBtn,
                  opacity: canSubmit ? 1 : 0.5,
                  cursor: canSubmit ? 'pointer' : 'not-allowed',
                }}
              >
                {submit === 'sending' ? 'Sending…' : 'Contribute'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>,
    document.body,
  );
}

const primaryBtn: React.CSSProperties = {
  fontFamily: FONT.ui,
  background: GOLD,
  color: GOLD_INK,
  border: 0,
  borderRadius: RADIUS.sm,
  padding: '8px 18px',
  fontSize: 13,
  fontWeight: 600,
  boxShadow: `0 0 18px ${hexA(GOLD, 0.28)}`,
  cursor: 'pointer',
};

const secondaryBtn: React.CSSProperties = {
  fontFamily: FONT.ui,
  background: SURFACE.raised,
  color: INK,
  border: `1px solid ${LINE2}`,
  borderRadius: RADIUS.sm,
  padding: '8px 16px',
  fontSize: 13,
  cursor: 'pointer',
};
